import { AppRole } from '../types/auth';

/** Paired LVO roles — hidden as separate columns; one shared LVO column instead. */
export const LVO_PAIRED_ROLE_NAMES: readonly AppRole[] = [
  AppRole.EXTERNAL_REQUESTOR_LVO,
  AppRole.PARTNER_CONTRIBUTOR_LVO,
];

const LVO_PAIRED_SET = new Set<string>(LVO_PAIRED_ROLE_NAMES);

/** Channel roles that can receive the paired LVO variant. */
export const LVO_ELIGIBLE_CHANNEL_ROLES: readonly AppRole[] = [
  AppRole.EXTERNAL_REQUESTOR,
  AppRole.PARTNER_CONTRIBUTOR,
];

const CHANNEL_TO_LVO: Readonly<Record<string, AppRole>> = {
  [AppRole.EXTERNAL_REQUESTOR]: AppRole.EXTERNAL_REQUESTOR_LVO,
  [AppRole.PARTNER_CONTRIBUTOR]: AppRole.PARTNER_CONTRIBUTOR_LVO,
};

export const LVO_COLUMN_KEY = 'LVO';
export const LVO_COLUMN_LABEL = 'LVO';
export const LVO_COLUMN_ACCESSIBILITY_LABEL =
  'Local Vertiv Office (LVO) — opp and quote visible for channel roles';

export function isLvoPairedRoleName(roleName: string): boolean {
  return LVO_PAIRED_SET.has(roleName);
}

export function roleNamesForUsersTable(roleNames: readonly string[]): string[] {
  return roleNames.filter(name => !isLvoPairedRoleName(name));
}

export function hasLvoEligibleChannelRole(
  roleNames: ReadonlySet<string>,
): boolean {
  return LVO_ELIGIBLE_CHANNEL_ROLES.some(role => roleNames.has(role));
}

export function isLvoColumnChecked(roleNames: ReadonlySet<string>): boolean {
  return LVO_PAIRED_ROLE_NAMES.some(role => roleNames.has(role));
}

/** Apply LVO paired roles for every eligible channel role currently selected. */
export function applyLvoRolesToSelection(roleNames: Set<string>): void {
  for (const channelRole of LVO_ELIGIBLE_CHANNEL_ROLES) {
    if (roleNames.has(channelRole)) {
      roleNames.add(CHANNEL_TO_LVO[channelRole]);
    }
  }
}

/** Remove both paired LVO roles from a selection. */
export function removeLvoRolesFromSelection(roleNames: Set<string>): void {
  for (const lvoRole of LVO_PAIRED_ROLE_NAMES) {
    roleNames.delete(lvoRole);
  }
}

/** When a channel role is cleared, drop its paired LVO role too. */
export function removePairedLvoWhenChannelRemoved(
  roleNames: Set<string>,
  removedChannelRole: string,
): void {
  const paired = CHANNEL_TO_LVO[removedChannelRole];
  if (paired) {
    roleNames.delete(paired);
  }
}

export function toggleLvoColumnInSelection(roleNames: Set<string>): void {
  if (isLvoColumnChecked(roleNames)) {
    removeLvoRolesFromSelection(roleNames);
  } else {
    applyLvoRolesToSelection(roleNames);
  }
}
